import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiRequest, downloadFile } from '../api/client'

// Filter dropdown — matches the plan's Reviewer UI status list. Two states
// exist on the backend but are intentionally hidden from the filter:
//   - ai_reviewed (Phase 2 only; reserved for the AI service account)
//   - shopify_customer_deleted (system-set via webhooks; not usually filtered on)
// Records in those states still render with their badge if they show up.
const STATUSES = [
  'submitted',
  'under_review',
  'pending_company_match',
  'approved',
  'rejected',
  'hq_review',
  'fraud_review',
  'revoked',
  're_verification_requested'
]

const STATUS_STYLES = {
  submitted:                'bg-slate-50 text-slate-700 border-slate-200',
  under_review:             'bg-sky-50 text-sky-800 border-sky-200',
  pending_company_match:    'bg-indigo-50 text-indigo-800 border-indigo-200',
  ai_reviewed:              'bg-violet-50 text-violet-800 border-violet-200',
  approved:                 'bg-emerald-50 text-emerald-800 border-emerald-200',
  rejected:                 'bg-rose-50 text-rose-800 border-rose-200',
  hq_review:                'bg-amber-50 text-amber-800 border-amber-200',
  fraud_review:             'bg-red-100 text-red-900 border-red-300',
  shopify_customer_deleted: 'bg-orange-50 text-orange-800 border-orange-200',
  revoked:                  'bg-stone-100 text-stone-800 border-stone-300',
  re_verification_requested:'bg-cyan-50 text-cyan-800 border-cyan-200'
}

const TYPE_STYLES = {
  trade:       'bg-brand-50 text-brand-700 border-brand-200',
  hospitality: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200'
}

// BBE-234: Shopify sync outcome.
const SYNC_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  failed:  'bg-red-100 text-red-900 border-red-300'
}
const SYNC_LABELS = { pending: 'sync pending', failed: 'sync failed' }

function Badge({ value, styles }) {
  if (!value) return null
  const cls = styles[value] || 'bg-slate-50 text-slate-700 border-slate-200'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${cls}`}>
      {value.replace(/_/g, ' ')}
    </span>
  )
}

// Surface only states that need attention in the list — 'synced' is the
// expected happy path and would just add noise next to "approved".
function SyncBadge({ state }) {
  if (!state || state === 'synced') return null
  const cls = SYNC_STYLES[state] || 'bg-slate-50 text-slate-700 border-slate-200'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${cls}`}>
      {SYNC_LABELS[state] || state}
    </span>
  )
}

export default function TradeApplicationsListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const status = searchParams.get('status') || ''
  const type   = searchParams.get('application_type') || ''
  const q      = searchParams.get('q') || ''

  const [qInput, setQInput] = useState(q)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')

  useEffect(() => {
    setLoading(true)
    const qs = new URLSearchParams()
    if (status) qs.set('status', status)
    if (type)   qs.set('application_type', type)
    if (q)      qs.set('q', q)
    apiRequest(`/api/v1/trade_applications${qs.toString() ? `?${qs}` : ''}`)
      .then(({ trade_applications }) => setApplications(trade_applications))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [status, type, q])

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    updateParam('q', qInput.trim())
  }

  const handleExport = async () => {
    setExporting(true)
    setExportError('')
    try {
      const qs = new URLSearchParams()
      if (status) qs.set('status', status)
      if (type)   qs.set('application_type', type)
      if (q)      qs.set('q', q)
      const url = `/api/v1/trade_applications/export${qs.toString() ? `?${qs}` : ''}`
      await downloadFile(url)
    } catch (err) {
      setExportError(err.message)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Trade &amp; Hospitality applications</h1>
          <p className="text-sm text-slate-600 mt-1">Review Trade and Hospitality applications.</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          {exportError ? (
            <p className="text-xs text-rose-700">{exportError}</p>
          ) : null}
        </div>
      </div>

      <div className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
        <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[220px]">
          <label className="label" htmlFor="search">Search</label>
          <input
            id="search"
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Company, email, or name"
            className="input"
          />
        </form>
        <div className="min-w-[180px]">
          <label className="label" htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={status}
            onChange={(e) => updateParam('status', e.target.value)}
            className="input"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="label" htmlFor="type-filter">Type</label>
          <select
            id="type-filter"
            value={type}
            onChange={(e) => updateParam('application_type', e.target.value)}
            className="input"
          >
            <option value="">All types</option>
            <option value="trade">Trade</option>
            <option value="hospitality">Hospitality</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-600">Loading…</p>
      ) : error ? (
        <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
      ) : applications.length === 0 ? (
        <div className="card p-8 text-center text-slate-600">No applications match.</div>
      ) : (
        <>
          {/* Mobile */}
          <div className="sm:hidden space-y-3">
            {applications.map((a) => (
              <Link
                key={a.id}
                to={`/trade_applications/${a.id}`}
                className="card p-4 block hover:border-brand-500 transition"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-medium text-slate-900">{a.company_name}</div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge value={a.status} styles={STATUS_STYLES} />
                    <SyncBadge state={a.sync_state} />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <span>#{a.id}</span>
                  <Badge value={a.application_type} styles={TYPE_STYLES} />
                  <span>· {new Date(a.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm text-slate-700 truncate">
                  {a.full_name} &lt;{a.email_address}&gt;
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden sm:block card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Submitted</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {applications.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600">{a.id}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{a.company_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <Badge value={a.application_type} styles={TYPE_STYLES} />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {a.full_name}
                        <div className="text-xs text-slate-500">{a.email_address}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <Badge value={a.status} styles={STATUS_STYLES} />
                          <SyncBadge state={a.sync_state} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                        {new Date(a.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/trade_applications/${a.id}`}
                          className="text-sm font-medium text-brand-600 hover:text-brand-700"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
